import { useEffect, useRef, useState } from "react";

export default function AboutPage() {
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoaded(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="bg-[#F7F4EE] text-[#1A1A1A] py-16 md:py-24 px-6 md:px-6 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto">

        {/* HEADER */}
        <div
          className={`max-w-3xl mb-5 md:mb-10 transition-all duration-700
          ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <p className="text-xs lg:text-md tracking-[0.3em] md:tracking-[0.35em] text-green-400 mb-3 md:mb-4">
            WHO ARE WE
          </p>
        </div>

        {/* HERO IMAGE */}
        <div className="relative mb-16 md:mb-28">

          <img
            src="https://images.unsplash.com/photo-1464226184884-fa280b87c399"
            alt="Regenerative agroforestry landscape"
            className="w-full h-[320px] sm:h-[420px] md:h-[600px] object-cover rounded-2xl"
          />

          {/* OVERLAY STORY CARD */}
          <div
            className={`
              relative md:absolute md:bottom-[-60px] 
              md:left-10 lg:left-20 
              mt-6 md:mt-0
              max-w-xl bg-white
              p-6 md:p-8 lg:p-10
              rounded-2xl shadow-2xl border border-green-100
              transition-all duration-700 delay-200
              ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}
            `}
          >

            <p className="text-base lg:text-lg leading-relaxed text-[#333] mb-6">
              A non-profit, non-political, and non-religious organization advancing climate resilience through community-driven action.
            </p>

            <div className="space-y-3">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-green-500">
                Trust Points
              </h4>

              <div className="grid gap-3">
                {[
                  "Verified impact tracking",
                  "Community-led implementation",
                  "Transparent funding model",
                  "Measurable climate outcomes",
                ].map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-green-50 rounded-xl px-4 py-3 border border-green-100"
                  >
                    <div className="w-2 h-2 rounded-full bg-green-400" />

                    <p className="text-sm md:text-base text-[#444] font-medium">
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FOUNDER FEATURE */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 lg:gap-20 items-center mt-20 md:mt-32
          transition-all duration-700 delay-500
          ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >

          {/* TEXT */}
          <div>

            <p className="text-xs lg:text-md tracking-[0.3em] md:tracking-[0.35em] text-green-400 mb-3 md:mb-4">
              FOUNDER'S INTENT
            </p>

            <h3 className="text-2xl md:text-3xl lg:text-4xl leading-snug font-medium mb-5 md:mb-6">
              Climate Change Is Already
              <span className="italic font-light text-green-500">
                {" "}Impacting Livelihoods.
              </span>
            </h3>

            <p className="text-base md:text-lg leading-relaxed text-[#333]">
              Across vulnerable communities, rising temperatures, land degradation, and declining agricultural productivity are affecting food systems, income security, and long-term resilience - especially for women and youth.
              <br /><br />
              Without intervention, these challenges deepen inequality and reduce future opportunities.
            </p>

          </div>

          {/* IMAGE */}
          <div className="relative">

            <img
              src="https://womenwhofarm.africa/wp-content/uploads/2021/07/farming-for-changing-1024x683.jpg"
              alt="African women farming"
              className="rounded-2xl w-full h-[320px] sm:h-[420px] md:h-[520px] object-cover"
            />

            <div className="absolute inset-0 rounded-2xl ring-1 ring-green-200"></div>

          </div>

        </div>

      </div>
    </section>
  );
}